import type { WorkbookDefinition } from "@erp/shared-types";
import { freeformFormulaText, storedFreeformFormula } from "./freeform/formula";
import { sheetKind, type FormulaForms } from "./sheetKinds";
import {
  columnReference,
  formulaBodyOffset,
  lexFormula,
  rowReference,
  storedTableReference,
  tableReference,
  type FormulaToken,
} from "./formulaLexer";
import { definitionSourceColumns } from "./plan";
import {
  resolveTable,
  tableItems,
  type TableColumns,
  type WorkbookTableItem,
} from "./tableReferences";

/** Rewrites tokens by span, leaving every other character exactly as typed. */
function rewriteBody(
  formula: string,
  rewrite: (token: FormulaToken) => string | null,
): string {
  let result = "";
  let offset = formulaBodyOffset(formula);
  for (const token of lexFormula(formula)) {
    const replacement = rewrite(token);
    if (replacement === null) continue;
    result += formula.slice(offset, token.span.start) + replacement;
    offset = token.span.end;
  }
  return result + formula.slice(offset);
}

/**
 * The Formula body users read, copy and type, without the `=`: a Row reference
 * for every Field (`[@Cases] * [@[Case Price]]`), whatever its origin, and a
 * Table reference by its table's current name (`'Supplier Prices'[SKU]`). A
 * stored Table reference to a removed table stays as stored.
 */
export function formulaBodyText(
  expression: string,
  tables: readonly WorkbookTableItem[] = [],
): string {
  return rewriteBody(expression, (token) => {
    if (token.kind === "column") return rowReference(token.name);
    if (token.kind !== "table" || token.table.kind !== "id") return null;
    const resolved = resolveTable(tables, token.table);
    return resolved.ok
      ? tableReference(resolved.item.name, token.column)
      : null;
  });
}

/** The Formula text users read, copy and type: Excel's `=` and `formulaBodyText`. */
export function formulaText(
  expression: string,
  tables: readonly WorkbookTableItem[] = [],
): string {
  return `=${formulaBodyText(expression, tables)}`;
}

/**
 * The stored form: every Row reference, and every Qualified reference to this
 * Worksheet's input (pasted from older text), becomes a Column reference. A
 * Qualified reference to any other input is kept as typed, so the Plan reports
 * it instead of silently redirecting it. A Table reference whose name matches
 * one table stores that table's id; any other is kept as typed, so the Plan
 * reports it.
 */
export function storedFormula(
  text: string,
  inputName: string,
  tables: readonly WorkbookTableItem[] = [],
): string {
  const input = inputName.toLowerCase();
  return rewriteBody(text, (token) => {
    if (token.kind === "column") return columnReference(token.name);
    if (token.kind === "table") {
      const resolved =
        token.table.kind === "name" ? resolveTable(tables, token.table) : null;
      return resolved?.ok
        ? storedTableReference(resolved.item.id, token.column)
        : null;
    }
    return token.kind === "qualified" && token.qualifier.toLowerCase() === input
      ? columnReference(token.name)
      : null;
  });
}

/** The input columns a Formula reads, each once, in order of first appearance. */
export function formulaInputReferences(
  expression: string,
  inputColumns: readonly string[],
): string[] {
  return [
    ...new Set(
      lexFormula(expression).flatMap((token) =>
        token.kind === "column" && inputColumns.includes(token.name)
          ? [token.name]
          : [],
      ),
    ),
  ];
}

type FormulaForm = "text" | "stored";

/** How a Definition's Formulas are rewritten: its tables' columns, and the Definition it was edited from. */
export interface FormulaFormOptions {
  /**
   * Each table's columns in their current order (a Plan's `tables`), for
   * freeform references by column name. Worksheets' and inline Sources'
   * are known from the Definition; any other Source's only from a read.
   */
  columns?: TableColumns;
  /**
   * The stored Definition this one was edited from: a freeform cell whose
   * text is unchanged from it keeps its stored form, so a reference stored
   * by position is re-stored by name only when its own cell is edited.
   */
  base?: WorkbookDefinition;
}

/**
 * The columns of every table the Definition alone knows (each Worksheet's
 * declared output, each inline Source's), then those `columns` has.
 */
export function definitionTableColumns(
  definition: WorkbookDefinition,
  columns?: TableColumns,
): TableColumns {
  const known = new Map<string, readonly { name: string }[]>(
    [...definitionSourceColumns(definition)].map(([id, names]) => [
      id,
      names.map((name) => ({ name })),
    ]),
  );
  for (const sheet of definition.sheets)
    known.set(sheet.id, sheetKind(sheet).declaredOutput());
  for (const [id, schema] of columns ?? []) known.set(id, schema);
  return known;
}

/** A freeform sheet's cells as stored in `base`, by address. */
function baseCells(
  base: WorkbookDefinition | undefined,
  sheetId: string,
): Readonly<Record<string, string>> {
  const sheet = base?.sheets.find((item) => item.id === sheetId);
  return sheet?.kind === "freeform" ? sheet.cells : {};
}

function formulaForms(
  form: FormulaForm,
  inputName: string,
  sheetId: string,
  tables: readonly WorkbookTableItem[],
  base: Readonly<Record<string, string>>,
): FormulaForms {
  return form === "text"
    ? {
        row: (expression) => formulaText(expression, tables),
        cell: (input) => freeformFormulaText(input, tables),
      }
    : {
        row: (expression) => storedFormula(expression, inputName, tables),
        cell: (input, address) => {
          const before = base[address];
          return before !== undefined &&
            freeformFormulaText(before, tables) === input
            ? before
            : storedFreeformFormula(input, tables, sheetId);
        },
      };
}

function mapFormulas(
  definition: WorkbookDefinition,
  form: FormulaForm,
  options: FormulaFormOptions,
): WorkbookDefinition {
  const tables = tableItems(
    definition,
    definitionTableColumns(definition, options.columns),
  );
  const names = new Map(tables.map((item) => [item.id, item.name]));
  return {
    ...definition,
    sheets: definition.sheets.map((sheet) => {
      const kind = sheetKind(sheet);
      const inputId = kind.input();
      const input = (inputId && names.get(inputId)) ?? "";
      const forms = formulaForms(
        form,
        input,
        sheet.id,
        tables,
        baseCells(options.base, sheet.id),
      );
      // A Formula Validation is a row Formula over the Result, on every kind of Worksheet.
      const validations = sheet.validations && {
        validations: sheet.validations.map((validation) =>
          validation.kind === "formula"
            ? { ...validation, expression: forms.row(validation.expression) }
            : validation,
        ),
      };
      return { ...kind.mapFormulas(forms), ...validations };
    }),
  };
}

/**
 * The Definition with every Formula as the text users read (for the agent),
 * a freeform reference by column name by its column's current letter where
 * `options.columns` or the Definition knows the table's columns.
 */
export function definitionWithFormulaText(
  definition: WorkbookDefinition,
  options: FormulaFormOptions = {},
): WorkbookDefinition {
  return mapFormulas(definition, "text", options);
}

/**
 * The Definition with every Formula in stored form, whichever form it was
 * written in. An unchanged Formula stores exactly as before; pass the same
 * `columns` as its text was made with, and `base` when it was edited from a
 * stored Definition.
 */
export function definitionWithStoredFormulas(
  definition: WorkbookDefinition,
  options: FormulaFormOptions = {},
): WorkbookDefinition {
  return mapFormulas(definition, "stored", options);
}
